import React, { useEffect } from "react";
import { Phone, MessageCircle, CalendarClock, Star } from "lucide-react";
import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import PointsRegistrationForm from "@/components/PointsRegistrationForm";
import TrustBarPuntos from "@/components/TrustBarPuntos";
import PointsCalculator from "@/components/PointsCalculator";
import { whatsappLink, HEADER_LOGO } from "@/lib/faro";
import { useSeo, SITE_URL } from "@/lib/seo";
import { PUNTOS_EUR } from "@/lib/courses";
import scheduleSeed from "@/data/schedule.generated.json";

// Landing de conversión para Google Ads y enlaces de WhatsApp.
// noindex + fuera del sitemap: no compite con /recuperacion-de-puntos-vigo en orgánico.
// El formulario es el mismo componente (dispara form_submit_puntos y reserva_curso).

const TEL = "+34986292455";
const TEL_LABEL = "986 29 24 55";

function nextPuntosDate() {
  const list = (scheduleSeed && scheduleSeed.convocatorias && scheduleSeed.convocatorias.A) || [];
  const today = new Date().toISOString().slice(0, 10);
  const next = list.map((c) => c.startDay).filter(Boolean).sort().find((d) => d >= today);
  if (!next) return null;
  const [y, m, d] = next.split("-").map(Number);
  const raw = new Date(y, m - 1, d).toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
  return { iso: next, label: raw.charAt(0).toUpperCase() + raw.slice(1) };
}

export default function ReservaPuntosPage() {
  const next = nextPuntosDate();
  const wa = whatsappLink(
    "34637570075",
    `Hola, quiero reservar plaza en el curso de recuperación de puntos${next ? ` del ${next.label}` : ""}.`
  );

  useSeo({
    title: `Reserva tu curso de recuperación de puntos en Vigo · ${PUNTOS_EUR}`,
    description:
      "Reserva plaza en el curso de recuperación de puntos de Autoescuelas Faro en Vigo: 1 día presencial + 3 h online, sin examen. Centro autorizado DGT nº 360024.",
    canonical: `${SITE_URL}/reserva-puntos`,
  });

  useEffect(() => {
    const m = document.createElement("meta");
    m.setAttribute("name", "robots");
    m.setAttribute("content", "noindex,follow");
    document.head.appendChild(m);
    return () => m.remove();
  }, []);

  const li = "flex items-start gap-2.5";
  const ic = "text-[var(--faro-red)] shrink-0 mt-0.5";

  return (
    <div className="min-h-screen pb-[calc(env(safe-area-inset-bottom)+88px)] sm:pb-0" data-testid="reserva-puntos-page">
      <header className="bg-white border-b border-[rgba(30,42,143,0.12)]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-11 h-11 rounded-full overflow-hidden shrink-0 bg-[var(--faro-blue-deep)]">
              <img src={HEADER_LOGO} alt="Autoescuelas Faro" width="44" height="44" className="w-full h-full object-cover scale-105" />
            </span>
            <span className="leading-tight min-w-0">
              <span className="block font-display font-black text-[var(--faro-blue-deep)]">Autoescuelas Faro</span>
              <span className="block text-xs font-semibold text-[var(--faro-text-soft)]">Centro autorizado DGT nº 360024</span>
            </span>
          </div>
          <a
            href={`tel:${TEL}`}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--faro-blue-deep)] text-white font-bold px-4 py-2 text-sm shrink-0"
            data-testid="rpz-call-top"
          >
            <Phone size={16} /> <span className="hidden sm:inline">{TEL_LABEL}</span>
            <span className="sm:hidden">Llamar</span>
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-6 sm:py-10">
        <span className="eyebrow">Recuperación de puntos · Vigo</span>
        <h1 className="font-display font-black tracking-tighter text-3xl sm:text-5xl mt-2 leading-tight">
          Elige y reserva plaza en tu curso de puntos
        </h1>

        <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2.5 text-base sm:text-lg" data-testid="rpz-facts">
          <li className={li}>
            <CalendarClock size={22} className={ic} />
            <span>
              {next ? <>Próximo curso: <strong>{next.label}</strong></> : <>Convocatoria <strong>cada mes</strong></>}
            </span>
          </li>
          <li className={li}>
            <Star size={22} className={ic} />
            <span><strong>4,6★ en Google</strong> · 148 reseñas</span>
          </li>
        </ul>

        <PointsCalculator />

        <section id="reserva" className="mt-8 scroll-mt-4">
          <PointsRegistrationForm sideTitle="Reserva tu plaza" />
        </section>

        <div className="mt-10">
          <TrustBarPuntos />
        </div>

        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <a
            href={wa}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white font-bold px-5 py-3"
            data-testid="rpz-whatsapp"
          >
            <MessageCircle size={18} /> Reservar por WhatsApp
          </a>
          <a
            href={`tel:${TEL}`}
            className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--faro-blue-deep)] text-[var(--faro-blue-deep)] font-bold px-5 py-3"
            data-testid="rpz-call"
          >
            <Phone size={18} /> Llamar ahora
          </a>
        </div>

        <p className="mt-8 text-center text-xs text-[var(--faro-text-soft)]">
          Automovilística Faro S.L. · Centro Comercial 3 Plaza América, local 12, Vigo ·{" "}
          <a href="/politica-privacidad" className="underline">Privacidad</a>
        </p>
      </main>

      <FloatingWhatsapp />
    </div>
  );
}
